import React, { useEffect, useRef } from 'react';

// Style
import './Tree.scss';
import './Thesaurus.scss';
import 'assets/css/rc-tree.min.css';

// Lib
import { shallowEqual } from 'react-redux';
import { useAppDispatch, useAppSelector } from 'lib/hooks/useDispatch';

// Classnames
import clsx from 'clsx';

// Actions
import { openModal } from 'reducers/modal';
import * as actionsCreatorSearch from '../Search/reducer';
import * as actionsCreator from 'pages/Thesaurus/reducer';

// Lib
import { useWindowSize } from 'lib/hooks';
import { Scrolling } from 'lib/hooks/scrolling';
import {
	filterFn,
	listTree,
	findElement,
	switcherIcon,
	setRcTreeInstance
} from 'lib/thesaurus';

// Components
import Tree from 'rc-tree';
import NoData from 'components/NoData';
import Loader from 'components/UiKit/Loader';
import FieldCheckbox from 'components/UiKit/FieldCheckbox';
import EmptyTree from 'pages/Thesaurus/components/EmptyTree';
import TargetBox from 'pages/Search/pages/BioSearch/component/TargetBox';
import TargetDetailsContainer from 'pages/Thesaurus/components/TargetDetailsContainer';

// Type
import { TypeTarget } from 'pages/Thesaurus/type';

export const DEFAULT_REQUEST_CONFIG: RequestConfig = { familyId: 0, isInit: true };

type Key = string | number;

interface DataNode {
	key: Key;
	title?: React.ReactNode;
	children?: DataNode[];

	[prop: string]: any;
}

interface EventDataNode extends DataNode {
	expanded?: boolean;
	selected?: boolean;
	checked?: boolean;
	halfChecked?: boolean;
	dragOver?: boolean;
	dragOverGapTop?: boolean;
	dragOverGapBottom?: boolean;
	pos: string;
	active?: boolean;
	eventKey?: string;
	parent?: DataNode;
}

export interface CustomProps {
	className?: string;
	'family-id'?: number;
	dangerouslySetInnerHTML?: { __html: string };
}

export interface CustomElement {
	$$typeof: Symbol;
	props: CustomProps;
	ref: React.Ref<any> | null;
	key: null | string | number;
	type: keyof JSX.IntrinsicElements | React.JSXElementConstructor<any>;
}

export type Extra = {
	name?: string;
	navKey?: string;
	familyId?: number;
	synonyms?: string;
	targetId?: number;
	className?: string;
	showInEbcHeader?: boolean;
}

type RequestConfig = {
	isInit: boolean;
	familyId: number;
}

export type TreeNode = {
	key: string,
	pos?: string,
	extra?: Extra,
	value?: string,
	loaded?: boolean,
	isLeaf?: boolean,
	active?: boolean,
	checked?: boolean,
	loading?: boolean,
	dragOver?: boolean,
	expanded?: boolean,
	filterSyn?: string,
	name?: string,
	synonyms?: string,
	selected?: boolean,
	showLabel?: boolean,
	filterName?: string,
	targetCount?: string,
	halfChecked?: boolean,
	title?: CustomElement,
	dragOverGapTop?: boolean,
	dragOverGapBottom?: boolean,

	children?: TreeNode[];
	expandedKeys?: (string | number)[];
}

const MAX_SELECTED_KEYS: number = 61;
const TARGET_CLASSNAME: string = 'leaf';
const INITIAL_EXPAND_TREE: string[] = ['key_1'];

const Thesaurus: React.FC = () => {
	const dispatch = useAppDispatch();
	const ref = useRef<Tree<TreeNode>>(null);

	const checkedKeys: string[] = [];
	const localStoreKey = 'checkedKeys';
	const checkedKeysLocalStore: string[] | [] = JSON.parse(localStorage.getItem(localStoreKey) || '[]');
	let dynamicKeys: string[] = checkedKeysLocalStore || [];

	const { width } = useWindowSize();
	const isDesktop = width < 767;

	const {
		data,
		tags,
		expand,
		autoExpand,
		showAllData,
		input: value,
		isInitLoading,
		loadingSearchBio,
		toggleTargetDetails
	} = useAppSelector(
		(store) => store.thesaurus,
		shallowEqual
	);
	const isSearch = useAppSelector(
		(store) => store.search.isSearch,
		shallowEqual
	);

	const isScrolling = Scrolling({ limit: 40 });
	const isLimitTags = tags.length < MAX_SELECTED_KEYS;
	const mainData = !showAllData ? findElement(data, value) : data;

	useEffect(() => {
		if (!data.length) {
			dispatch(actionsCreator.getThesaurusById(DEFAULT_REQUEST_CONFIG));

			const checkedKeys = checkedKeysLocalStore.length
				? checkedKeysLocalStore
				: INITIAL_EXPAND_TREE;
			dispatch(actionsCreator.handleExpand(INITIAL_EXPAND_TREE));
		}

		setRcTreeInstance(ref.current || null);
	}, [data, dispatch]);

	useEffect(() => {
		if (!isSearch) {
			return;
		}

		const keyToScroll = dynamicKeys[0]
			? { key: dynamicKeys[0] }
			: { key: '' };

		if (!!ref?.current) {
			ref.current.scrollTo(keyToScroll);
		}
		dispatch(actionsCreatorSearch.handlerChangeIsSearch(false));
	}, [isSearch, ref.current]);

	const onLoadData = (treeNode: EventDataNode) => {
		return new Promise(resolve => {
			const name = treeNode.extra.name;
			const familyId = treeNode?.extra?.familyId;
			if (name === 'others') {
				resolve('');
			}
			if (
				familyId &&
				treeNode.extra.className !== TARGET_CLASSNAME
			) {
				dispatch(actionsCreator.getThesaurusById({
					resolve: resolve,
					familyId: familyId,
					pos: treeNode?.pos.split('-')
				}));
			}
		});
	};

	const filterTreeNode = (treeNode: TreeNode) => {
		return filterFn(
			treeNode?.filterName || '',
			treeNode?.filterSyn || '',
			value
		);
	};

	const getElementByIndex = (data: TypeTarget[], path: any): TypeTarget | undefined => {
		const tail = path.slice(1);
		const firstElementPath = path[0];

		if (!tail.length) {
			return data[firstElementPath];
		}

		if (data[firstElementPath]?.children) {
			return getElementByIndex(data[firstElementPath]?.children, tail);
		}

		return data[firstElementPath];
	};

	const getModal = () => {
		dispatch(openModal({
			type: 'limitExceeded'
		}));
	};

	const getItems = (checkedKeys: string[] = [], data: TypeTarget[] = []) => {
		const convertKey =
			(checkedKeys.length &&
				checkedKeys.map((item: any) => item.split('_').slice(1))) ||
			[];
		const result = convertKey
			.map((item, i: number) => ({
				...getElementByIndex(data, item),
				key: checkedKeys[i]
			}))
			.filter(Boolean);

		const filteredOnlyTargets = result.filter(
			(item: TypeTarget) => item.className === TARGET_CLASSNAME
		);

		if (filteredOnlyTargets.length >= MAX_SELECTED_KEYS + 1) {
			getModal();
		} else {
			dispatch(actionsCreator.handlerUpdateTags(filteredOnlyTargets));
		}

		return filteredOnlyTargets;
	};

	const onCheck = (checkedKeys: any) => {
		const tagsArray = getItems(checkedKeys, mainData);

		if (tagsArray.length >= MAX_SELECTED_KEYS) {
			getModal();
		} else {
			dispatch(actionsCreator.handlerChangeCheckedGtf(checkedKeys));
		}
	};

	const handlerConfirmShowAllData = () => {
		dispatch(openModal({
			color: 'error',
			type: 'confirm',
			text: `All changes will be deleted`,
			actionSubmit: handlerChangeShowAllData
		}));
	};

	const handlerChangeShowAllData = () => {
		dispatch(actionsCreator.handleExpand([]));
		dispatch(actionsCreator.handlerUpdateTags([]));
		dispatch(actionsCreator.handlerChangeCheckedGtf([]));
		dispatch(actionsCreator.handlerChangeAutoExpand(true));

		dispatch(actionsCreator.handlerChangeShowAllData());
		dispatch(actionsCreator.handlerClearBAData());

		localStorage.setItem(localStoreKey, JSON.stringify([]));
	};

	const onExpand = (checkedKeys: any) => {
		dispatch(actionsCreator.handlerChangeAutoExpand(false));

		localStorage.setItem(localStoreKey, JSON.stringify(checkedKeys));
		dispatch(actionsCreator.handleExpand(checkedKeys));
	};

	const handlerClick = (event: any, node: any) => {
		const id = node?.extra?.targetId;
		if (id) {
			dispatch(actionsCreator.getTargetDetailsById(id));

			if (isDesktop) {
				dispatch(actionsCreator.toggleTargetDetails());
			}
		}
	};

	const treeData: TreeNode[] = listTree({
		key: 'key',
		data: mainData,
		targetBox: tags,
		inputValue: value,
		checkedKeys: checkedKeys,
		dynamicKeys: dynamicKeys,
		showAllData: !showAllData
	});

	if (!showAllData) {
		dynamicKeys = dynamicKeys.concat(expand);
	}

	if (!autoExpand) {
		dynamicKeys = expand;
	}

	const autoExpandParent = !!value && autoExpand;
	const expandedKeys = !!value ? dynamicKeys : expand;

	const cn = clsx(
		'tree',
		!!value ? 'tree__active' : ''
	);

	const cnTargetBox = clsx(
		'thesaurus__target-box',
		!isScrolling && tags?.length ? 'thesaurus__target-box--active' : ''
	);

	const cnThesaurusRight = clsx(
		'thesaurus__right-block pl5 relative',
		!isScrolling && tags?.length ? 'thesaurus__right-block--active' : ''
	);

	return (
		<div className="thesaurus">
			{isDesktop && toggleTargetDetails && <div className="thesaurus__target-details-mobile">
				<TargetDetailsContainer className="thesaurus__target-details-mobile-container" />
			</div>}

			{loadingSearchBio || isInitLoading
				? <div className="thesaurus__loader relative mt30 mb30">
					<Loader
						color="dark"
						type="small"
					/>
				</div>
				: data?.length ?
					<div className="flex flex-ai-start relative">
						<div className="thesaurus__left-block flex flex-direction-column pr5">
							<div
								className="thesaurus__title-block flex flex-ai-center flex-direction-column-mobile flex-jc-between full-width mb15 pr5">
								<h4 className="thesaurus__title mr10">
									EBC Target Tree
									{!!tags?.length &&
										<span className="ml10">(Selected targets: <b>{tags.length}</b> of <b>60</b>)</span>}
								</h4>
								<FieldCheckbox
									name="showAllData"
									value={showAllData}
									label="Show the full target tree"
									onChange={handlerConfirmShowAllData}
								/>
							</div>
							{treeData?.length ? <Tree
									virtual
									checkable
									ref={ref}
									selectable
									height={400}
									className={cn}
									itemHeight={25}
									onCheck={onCheck}
									onExpand={onExpand}
									treeData={treeData}
									expandAction="click"
									onClick={handlerClick}
									// loadData={onLoadData}
									checkedKeys={checkedKeys}
									defaultExpandAll={!!value}
									switcherIcon={switcherIcon}
									expandedKeys={expandedKeys}
									filterTreeNode={filterTreeNode}
									autoExpandParent={autoExpandParent}
								/>
								: <div
									style={{ height: '5rem' }}
									className="flex flex-ai-center flex-jc-center mt30 mb30"
								>
									<NoData text="Nothing was found" />
								</div>}
						</div>
						<div className={cnThesaurusRight}>
							<div className={cnTargetBox}>
								<TargetBox
									tags={tags}
									isLimit={!isLimitTags}
								/>
							</div>

							<TargetDetailsContainer />
						</div>
					</div>
					: <EmptyTree />}
		</div>
	);
};

export default Thesaurus;
